import type { FunctionsResponse } from "@supabase/functions-js";
import { isNil, normalizeAuthEmail } from "@mixitone/util";
import Debug from "debug";
import tauri, { type Tauri } from "lib/tauri";
import {
  createCrossSupabase,
  markTauriAccessTokenApplied,
  resetCrossSupabaseAuth,
  setTauriRefreshTokenPlaceholder,
} from "lib/supabaseClient";
import { AuthEvent, type Session, type User, SessionAdapter } from "./SessionAdapter";

const debug = Debug("TauriSessionAdapter");

export const supabaseOptions = {
  url: import.meta.env.VITE_SUPABASE_URL,
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
};

enum SessionEvent {
  SIGNED_IN = "session:signed_in",
  SIGNED_OUT = "session:signed_out",
  TOKEN_REFRESH = "session:token_refresh",
}

interface TauriUser {
  id: string;
  email: string;
  app_metadata: Record<string, any>;
  user_metadata: Record<string, any>;
  confirmation_sent_at?: string | null;
  email_confirmed_at?: string | null;
}

interface TauriSession {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at: number;
  token_type: string;
  user: TauriUser;
}

interface TauriSignUpResponse {
  user: TauriUser;
  session: TauriSession | null;
}

interface SessionEventPayload {
  event: SessionEvent;
  id: number;
  payload: null | TauriSession;
  windowLabel: string;
}

function unknownToError(e: unknown): Error {
  let error: Error;

  if (isNil(e)) {
    error = new Error("An unknown error occurred");
  } else if (e instanceof Error) {
    error = e;
  } else if (typeof e === "string") {
    error = new Error(e);
  } else if (typeof e === "object" && "error" in e) {
    error = new Error((e as any).error);
  } else {
    error = new Error("An unknown error occurred");
  }

  return error;
}

export class TauriSessionAdapter extends SessionAdapter {
  tauri: Tauri;

  constructor() {
    super();
    this.tauri = tauri;
  }

  get canSignUp() {
    return true;
  }
  get canSignIn() {
    return true;
  }
  get canSignOut() {
    return true;
  }
  get canResetPassword() {
    return false;
  }

  async getSession(): Promise<Session | null> {
    const session = (await this.tauri.invoke("plugin:session|get_session")) as
      | (Partial<Session> & { user?: User; exp?: number })
      | null;
    if (!session?.user) return null;

    // Rust get_session returns JWT claims + user, not a GoTrue-shaped session.
    // MFA readiness needs the raw access_token to read aal2 / totp amr claims.
    let accessToken = session.access_token;
    if (!accessToken) {
      try {
        const tokenResponse = await this.tauri.invoke<{ access_token: string }>(
          "plugin:session|get_access_token",
        );
        accessToken = tokenResponse.access_token;
      } catch (error) {
        debug("Failed to load access token for session", error);
      }
    }
    if (!accessToken) return null;

    return {
      access_token: accessToken,
      refresh_token: session.refresh_token ?? "na",
      expires_in: session.expires_in ?? 3600,
      expires_at: session.expires_at ?? session.exp,
      token_type: session.token_type ?? "bearer",
      user: session.user,
      adapter: "tauri",
    };
  }

  async getUser() {
    try {
      const client = await createCrossSupabase();
      const { error, data } = await client.auth.getUser();
      if (error || !data?.user) {
        return { error: error || new Error("No user returned"), data: null };
      }
      return { data: { user: data.user }, error: null };
    } catch (e) {
      return { error: unknownToError(e), data: null };
    }
  }

  // Email/password changes must go through edge step-up actions
  // (actions.account.*). Do not expose direct GoTrue updateUser shortcuts
  // on unlocked desktop sessions — inherit the base "not implemented" stubs.

  async updateName(name: string) {
    try {
      const client = await createCrossSupabase();
      const { error } = await client.auth.updateUser({ data: { name } });
      if (error) return { error };
      return { data: {} };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  private async persistMfaAccessToken(accessToken: string | undefined) {
    if (!accessToken) return;
    try {
      await this.tauri.invoke("plugin:session|update_access_token", { accessToken });
      markTauriAccessTokenApplied(accessToken);
      const client = await createCrossSupabase();
      await client.auth.setSession({
        access_token: accessToken,
        refresh_token: "na",
      });
      client.realtime.setAuth(accessToken);
      const session = await this.getSession();
      if (session) {
        this.emit(AuthEvent.TOKEN_REFRESHED, { ...session, adapter: "tauri" });
      }
    } catch (error) {
      debug("Failed to persist MFA access token in Rust", error);
      throw error;
    }
  }

  async applyElevatedAccessToken(accessToken: string) {
    try {
      await this.persistMfaAccessToken(accessToken);
      return { data: undefined };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  async listTotpFactors() {
    try {
      const client = await createCrossSupabase();
      const { data, error } = await client.auth.mfa.listFactors();
      if (error) return { error };
      const factors = (data?.all ?? data?.totp ?? [])
        .filter((factor) => factor.factor_type === "totp")
        .map((factor) => ({
          id: factor.id,
          friendly_name: factor.friendly_name,
          status: factor.status as "verified" | "unverified",
        }));
      return { data: { factors } };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  async hasTotpFactor() {
    const result = await this.listTotpFactors();
    if (result.error) return { error: result.error };
    const enrolled = (result.data?.factors ?? []).some((factor) => factor.status === "verified");
    return { data: { enrolled } };
  }

  async enrollTotp(friendlyName = "Authenticator") {
    try {
      const client = await createCrossSupabase();
      const { data, error } = await client.auth.mfa.enroll({
        factorType: "totp",
        friendlyName,
        issuer: "MixItOne",
      });
      if (error) return { error };
      if (!data?.id || !data.totp?.qr_code || !data.totp?.secret || !data.totp?.uri) {
        return { error: new Error("MFA enrollment did not return a factor") };
      }
      return {
        data: {
          factorId: data.id,
          qrCode: data.totp.qr_code,
          secret: data.totp.secret,
          uri: data.totp.uri,
        },
      };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  async verifyTotpEnrollment(factorId: string, code: string) {
    try {
      const client = await createCrossSupabase();
      const { data, error } = await client.auth.mfa.challengeAndVerify({
        factorId,
        code,
      });
      if (error) return { error };
      await this.persistMfaAccessToken(data?.access_token);
      return { data: { accessToken: data?.access_token } };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  async challengeAndVerifyTotp(factorId: string, code: string) {
    try {
      const client = await createCrossSupabase();
      const { data, error } = await client.auth.mfa.challengeAndVerify({
        factorId,
        code,
      });
      if (error) return { error };
      await this.persistMfaAccessToken(data?.access_token);
      return { data: { accessToken: data?.access_token } };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  async unenrollTotp(factorId: string) {
    try {
      const client = await createCrossSupabase();
      const { error } = await client.auth.mfa.unenroll({ factorId });
      if (error) return { error };
      return { data: undefined };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  async initialize() {
    const listeners = await Promise.all(
      Object.values(SessionEvent).map((event) =>
        this.tauri.listen(event, (payload) => this.handle(event, payload as any)),
      ),
    );

    debug("TauriSessionAdapter initialized", listeners);
    await this.tauri.invoke("plugin:session|load_session", {
      url: supabaseOptions.url,
      apiKey: supabaseOptions.anonKey,
    });
  }

  async signInWithPassword(email: string, password: string) {
    try {
      const response = await this.tauri.invoke<TauriSession>("plugin:session|sign_in_with_password", {
        email: normalizeAuthEmail(email),
        password,
      });

      const session = { ...response, adapter: "tauri" };

      if (response.access_token) {
        setTauriRefreshTokenPlaceholder(response.refresh_token);
        void this.syncSupabaseAuth();
        this.emit(AuthEvent.SIGNED_IN, session);
      }

      return {
        error: null,
        data: {
          session: session,
          user: response.user,
        },
      };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  async signUp(
    email: string,
    password: string,
    options?: {
      data: {
        name: string;
        country?: string;
        timezone?: string;
        inviteToken?: string;
        registerClubData?: Record<string, any>;
      };
    },
  ) {
    try {
      const response = await this.tauri.invoke<TauriSignUpResponse>("plugin:session|sign_up", {
        email: normalizeAuthEmail(email),
        password,
        data: options?.data ?? {},
      });

      const session = response.session ? { ...response.session, adapter: "tauri" } : null;

      if (session?.access_token) {
        setTauriRefreshTokenPlaceholder(session.refresh_token);
        void this.syncSupabaseAuth();
        this.emit(AuthEvent.SIGNED_IN, session);
      }

      return {
        error: null,
        data: {
          user: response.user,
          session,
        },
      };
    } catch (e) {
      return { error: unknownToError(e), data: null };
    }
  }

  async resetPasswordForEmail(email: string, redirectTo: string) {
    await this.tauri.invoke("plugin:session|reset_password_for_email", {
      email: normalizeAuthEmail(email),
      redirectTo,
    });
    return { error: null, data: {} };
  }

  async signOut() {
    try {
      await this.tauri.invoke("plugin:session|sign_out");
      return { data: undefined, error: null };
    } catch (e) {
      return { error: unknownToError(e) };
    }
  }

  private sessionFromEvent(payload: SessionEventPayload | TauriSession): TauriSession | null {
    const session =
      (payload as SessionEventPayload).payload !== undefined
        ? (payload as SessionEventPayload).payload
        : (payload as TauriSession);
    return session ?? null;
  }

  private async syncSupabaseAuth() {
    try {
      await createCrossSupabase();
    } catch (error) {
      debug("Failed to sync Supabase auth from Tauri session", error);
    }
  }

  handle(event: string, payload: SessionEventPayload | TauriSession) {
    switch (event) {
      case SessionEvent.SIGNED_IN: {
        const session = this.sessionFromEvent(payload);
        if (!session) {
          debug(event, payload);
          throw new Error("No payload");
        }
        setTauriRefreshTokenPlaceholder(session.refresh_token);
        void this.syncSupabaseAuth();
        this.emit(AuthEvent.SIGNED_IN, { ...session, adapter: "tauri" });
        break;
      }
      case SessionEvent.SIGNED_OUT:
        resetCrossSupabaseAuth();
        this.emit(AuthEvent.SIGNED_OUT, null);
        break;
      case SessionEvent.TOKEN_REFRESH: {
        const session = this.sessionFromEvent(payload);
        if (!session) throw new Error("No payload");
        setTauriRefreshTokenPlaceholder(session.refresh_token);
        this.emit(AuthEvent.TOKEN_REFRESHED, { ...session, adapter: "tauri" });
        void this.syncSupabaseAuth();
        break;
      }
    }
  }

  async rpc<T>(
    functionName: string,
    args: any,
  ): Promise<
    | {
        error: undefined;
        data: T;
      }
    | { error: Error; data: undefined }
  > {
    const response = await tauri.invoke("plugin:session|rpc", { functionName, args });
    return response as any;
  }

  async edgeFunction(functionName: string, body: any): Promise<FunctionsResponse<any>> {
    debug("edgeFunction", functionName);
    try {
      const response = await tauri.invoke("plugin:session|edge_function", { functionName, body });

      if (typeof response === "string") {
        return { data: JSON.parse(response), error: null };
      }

      return { data: response, error: null };
    } catch (e) {
      const error = unknownToError(e);
      // Rust often surfaces the response body as the error string, e.g. {"error":"..."}.
      let data: unknown = null;
      try {
        data = JSON.parse(error.message);
      } catch {
        data = null;
      }
      return { data, error } as FunctionsResponse<any>;
    }
  }
}
